import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Wrench, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * LifecycleSanitySection — admin-only maintenance action that finds
 * projects with a legacy `status="completed"` but a `lifecycle_status`
 * still stuck earlier (i.e. NOT `final_handover`), and back-fills
 * `lifecycle_status="final_handover"` so the PM board and Investor
 * Dashboard never drift out of sync.
 *
 * Two-step flow — Preview first, then Apply. Every applied change is
 * audited on the project.
 */
export default function LifecycleSanitySection() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/sanity/lifecycle-mismatch");
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const apply = async () => {
    if (!preview || preview.count === 0) return;
    const ok = window.confirm(
      `Set lifecycle_status="final_handover" on ${preview.count} project(s)? ` +
      "Each change is audited and can be reverted per-project."
    );
    if (!ok) return;
    setApplying(true);
    try {
      const r = await api.post("/settings/sanity/lifecycle-mismatch/apply");
      setLastResult(r.data);
      toast.success(`Fixed ${r.data.fixed} project${r.data.fixed === 1 ? "" : "s"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Fix failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="lifecycle-sanity-section">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wrench size={14} />
            <div className="label">Lifecycle status sanity fix</div>
          </div>
          <p className="muted text-xs mt-1 max-w-2xl">
            Some projects marked <code className="font-mono">status="completed"</code> never had
            their <code className="font-mono">lifecycle_status</code> advanced to <em>Final Handover</em>.
            That drift is why the Investor Dashboard sometimes shows fewer live sites than the Lifecycle Board.
            This action back-fills them in one pass.
          </p>
        </div>
        <button
          className="btn-ghost inline-flex items-center gap-1.5"
          onClick={refresh}
          disabled={loading}
          data-testid="lifecycle-sanity-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {preview && preview.count === 0 && (
        <div className="text-sm text-[#17663C] inline-flex items-center gap-2 mb-2"
             data-testid="lifecycle-sanity-clean">
          <CheckCircle2 size={14} /> No mismatches — every completed project is at Final Handover.
        </div>
      )}

      {preview && preview.count > 0 && (
        <>
          <div className="text-sm inline-flex items-center gap-2 mb-3"
               style={{ color: "#8A5410" }}
               data-testid="lifecycle-sanity-count">
            <AlertTriangle size={14} />
            {preview.count} mismatched project{preview.count === 1 ? "" : "s"}
          </div>
          <div className="border border-[var(--line)] rounded overflow-hidden mb-4 max-h-[280px] overflow-y-auto">
            <table className="data-table" data-testid="lifecycle-sanity-table">
              <thead>
                <tr>
                  <th className="text-left">Project</th>
                  <th className="text-left">Customer</th>
                  <th className="text-left">Current lifecycle</th>
                  <th className="text-left">Class</th>
                </tr>
              </thead>
              <tbody>
                {preview.projects.map((p) => (
                  <tr key={p.id} data-testid={`lifecycle-sanity-row-${p.id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td className="muted">{p.customer_name || "—"}</td>
                    <td className="font-mono text-xs muted">{p.lifecycle_status || "—"}</td>
                    <td className="text-xs muted">{p.business_classification || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            className="btn-primary inline-flex items-center gap-1.5"
            onClick={apply}
            disabled={applying}
            data-testid="lifecycle-sanity-apply"
          >
            <Wrench size={12} />
            {applying ? "Applying…" : `Fix ${preview.count} project${preview.count === 1 ? "" : "s"}`}
          </button>
        </>
      )}

      {lastResult && lastResult.fixed > 0 && (
        <div className="mt-4 text-xs muted" data-testid="lifecycle-sanity-result">
          Last run: fixed {lastResult.fixed} project{lastResult.fixed === 1 ? "" : "s"}.
        </div>
      )}
    </section>
  );
}
